import React, { useState, useEffect } from 'react';
import { X, Search, Package2, ArrowUp, ArrowDown, Filter, Trash2, AlertTriangle } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { getStockHistory, StockHistoryEntry, deleteStockHistoryEntry } from '../lib/stockItems';
import { format } from 'date-fns';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface StockHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function StockHistoryModal({
  isOpen,
  onClose
}: StockHistoryModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<StockHistoryEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showDeletedItems, setShowDeletedItems] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<StockHistoryEntry | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadHistory();
    }
  }, [isOpen]);

  const loadHistory = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getStockHistory(selectedBusiness.id);
      setHistory(data);
    } catch (err) {
      console.error('Error loading history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedType(null);
      setError(null);
    }
  };

  // Filter history based on search query, type, and deleted status
  const filteredHistory = history.filter(entry => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      entry.item_name.toLowerCase().includes(searchLower) ||
      entry.category_name?.toLowerCase().includes(searchLower) ||
      entry.notes?.toLowerCase().includes(searchLower) ||
      entry.created_by_email?.toLowerCase().includes(searchLower);

    const matchesType = !selectedType || entry.transaction_type === selectedType;
    
    // Filter by deleted status if necessary
    const matchesDeletedStatus = showDeletedItems || !entry.is_item_deleted;

    return matchesSearch && matchesType && matchesDeletedStatus;
  });

  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      setDeleteLoading(true);
      setError(null);

      // Get current stock quantity first
      const { data: stockItem, error: stockError } = await supabase
        .from('stock_items')
        .select('quantity')
        .eq('id', entryToDelete.item_id)
        .single();

      if (stockError && !stockError.message.includes('no rows returned')) {
        throw stockError;
      }

      // Item might be deleted, so we need to handle that case
      if (stockError && stockError.message.includes('no rows returned')) {
        // For deleted items, we can still delete history entries
        try {
          await deleteStockHistoryEntry(entryToDelete.id);
          setHistory(prev => prev.filter(entry => entry.id !== entryToDelete.id));
          setIsDeleteModalOpen(false);
          setEntryToDelete(null);
          await loadHistory();
        } catch (err) {
          console.error('Error deleting entry:', err);
          setError(err instanceof Error ? err.message : 'Failed to delete entry');
          return;
        }
      } else {
        // For active items, check quantities
        if (entryToDelete.transaction_type === 'Received' && 
            stockItem && stockItem.quantity < entryToDelete.quantity_change) {
          const proceed = window.confirm(
            `Deleting this entry will set the item quantity to 0 since there is not enough stock. Continue?`
          );
          
          if (!proceed) {
            setDeleteLoading(false);
            return;
          }
        }

        try {
          await deleteStockHistoryEntry(entryToDelete.id);
          setHistory(prev => prev.filter(entry => entry.id !== entryToDelete.id));
          setIsDeleteModalOpen(false);
          setEntryToDelete(null);
          await loadHistory();
        } catch (err) {
          // If deletion failed, show error but keep modal open
          console.error('Error deleting entry:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to delete entry';
          if (errorMessage.includes('negative stock quantity')) {
            setError('This entry cannot be deleted as it would result in invalid stock levels.');
          } else {
            setError('Failed to delete entry. Please try again.');
          }
          return;
        }
      }
    } catch (err) {
      console.error('Error deleting history entry:', err);
      setError('Failed to load current stock levels. Please try again.');
      return;
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[800px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Stock History</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search history..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div className="flex items-center space-x-2 overflow-x-auto pb-2 md:pb-0">
                <button
                  onClick={() => setSelectedType(null)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap ${
                    !selectedType
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setSelectedType('Received')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap ${
                    selectedType === 'Received'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Received
                </button>
                <button
                  onClick={() => setSelectedType('Withdrawn')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap ${
                    selectedType === 'Withdrawn'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Withdrawn
                </button>
                <button
                  onClick={() => setShowDeletedItems(!showDeletedItems)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap flex items-center ${
                    showDeletedItems
                      ? 'text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-100'
                      : 'bg-amber-100 text-amber-800 border border-amber-200'
                  }`}
                >
                  {showDeletedItems ? 'Hide' : 'Show'} Deleted Items
                  <AlertTriangle className="ml-1 h-3 w-3" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading history...</p>
                </div>
              </div>
            ) : error ? (
              <div className="p-6">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-12">
                <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No history found</h3>
                <p className="mt-2 text-sm text-gray-500">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Stock transactions will appear here'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredHistory.map((entry) => (
                  <div key={entry.id} className={`p-4 hover:bg-gray-50 ${entry.is_item_deleted ? 'bg-amber-50' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className={`p-2 rounded-lg ${
                          entry.transaction_type === 'Received'
                            ? 'bg-green-100'
                            : entry.transaction_type === 'Withdrawn'
                            ? 'bg-red-100'
                            : 'bg-gray-100'
                        }`}>
                          {entry.transaction_type === 'Received' ? (
                            <ArrowUp className={`h-5 w-5 ${
                              entry.transaction_type === 'Received'
                                ? 'text-green-600'
                                : entry.transaction_type === 'Withdrawn'
                                ? 'text-red-600'
                                : 'text-gray-600'
                            }`} />
                          ) : (
                            <ArrowDown className={`h-5 w-5 ${
                              entry.transaction_type === 'Received'
                                ? 'text-green-600'
                                : entry.transaction_type === 'Withdrawn'
                                ? 'text-red-600'
                                : 'text-gray-600'
                            }`} />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {entry.item_name}
                            {entry.is_item_deleted && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                Deleted Item
                              </span>
                            )}
                            <span className="ml-2 text-xs text-gray-500">
                              ({entry.category_name || 'Uncategorized'})
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                            {(entry.created_by_name || entry.created_by_email) && (
                              <span className="ml-2 font-medium">
                                by {entry.created_by_name || entry.created_by_email}
                              </span>
                            )}
                          </div>
                          {entry.notes && (
                            <div className="mt-2 text-sm text-gray-600">
                              {entry.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${
                          entry.transaction_type === 'Received'
                            ? 'text-green-600'
                            : entry.transaction_type === 'Withdrawn'
                            ? 'text-red-600'
                            : 'text-gray-600'
                        }`}>
                          {entry.transaction_type === 'Received' ? '+' : '-'}
                          {Math.abs(entry.quantity_change)} {entry.item_unit}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          ${entry.price.toFixed(2)} per unit
                        </div>
                        <div className="flex justify-end items-center space-x-4">
                          <span className="text-sm text-gray-500">${entry.price.toFixed(2)} per unit</span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                setEntryToDelete(entry);
                                setIsDeleteModalOpen(true);
                              }}
                              className="p-1 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setEntryToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete History Entry"
        message={error ? (
          <div className="text-red-600 whitespace-pre-line">{error}</div>
        ) : (
          <div>
            <p>Are you sure you want to delete this {entryToDelete?.transaction_type.toLowerCase() || ''} entry?</p>
            {entryToDelete?.is_item_deleted && (
              <p className="mt-2 text-amber-600 font-medium">
                This entry is for a deleted item.
              </p>
            )}
            <p className="mt-2">
              This will {entryToDelete?.is_item_deleted ? 'only remove the history entry' : 'update the stock quantity for this item'}.
            </p>
            {!entryToDelete?.is_item_deleted && (
              <p className="mt-2 text-yellow-600">
                Note: If the quantity would go below 0, it will be set to 0.
              </p>
            )}
            <p className="mt-2 font-medium">This action cannot be undone.</p>
          </div>
        )}
        loading={deleteLoading}
      />
    </div>
  );
}